import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { ModalComponent, UiComponent } from 'components';
import { IEvent, IPartner } from 'types';
import { EventQuery, PartnerQuery } from 'queries';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';

interface Props {
  event: IEvent | null;
  onClose: () => void;
}

function Partner({ event, onClose }: Props) {
  const { data } = PartnerQuery.useGetAll();
  const { mutate } = EventQuery.useUpdatePartners();
  const { data: partners } = EventQuery.useGetPartner(String(event?.id || ''));

  const [selectedPartners, setSelectedPartners] = useState<IPartner[]>([]);
  const partnerList = useMemo(() => {
    if (!data) return [];
    const partnerList = data as IPartner[];

    return partnerList.filter(
      (v) => !selectedPartners.find((d) => v.id === d.id),
    );
  }, [data, selectedPartners]);

  const handleSelect = (partner: IPartner) => {
    setSelectedPartners([...selectedPartners, partner]);
  };

  const handleRemove = (partner: IPartner) => {
    const newPartners = selectedPartners.filter(
      (item) => item.id !== partner.id,
    );
    setSelectedPartners(newPartners);
  };

  const handleSubmit = () => {
    if (!event) return;
    mutate(
      {
        id: event.id,
        partnerIds: selectedPartners.map((v) => v.id),
      },
      {
        onSuccess: () => {
          toast.success('파트너가 저장되었습니다.');
          onClose();
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  useEffect(() => {
    if (partners) {
      setSelectedPartners(partners);
    }
  }, [partners]);

  if (!data) return null;

  return (
    <>
      <ModalComponent.Portal>
        <ModalComponent.Background>
          <ModalComponent.Modal title="" handleClose={onClose} footer={<></>}>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px 0',
                '> p': {
                  marginTop: '12px',
                  fontWeight: 'bold',
                },
                '> div': {
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '8px',
                  '> div': {
                    position: 'relative',
                    display: 'flex',
                    width: '80px',
                    height: '80px',
                    background: '#f5f5f5',
                    borderRadius: '50%',
                    userSelect: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '> div': {
                      position: 'absolute',
                      width: '80px',
                      height: '80px',
                      background: 'black',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7,
                    },
                  },
                },
              }}
            >
              {selectedPartners.length > 0 && (
                <>
                  <p>선택 목록</p>
                  <div>
                    {selectedPartners.map((partner) => (
                      <Fragment key={partner.id}>
                        <PartnerItem
                          partner={partner}
                          hoverTitle="제거"
                          handleSelect={handleRemove}
                        />
                      </Fragment>
                    ))}
                  </div>
                </>
              )}
              <p>파트너 목록</p>
              <div>
                {partnerList.map((partner) => (
                  <Fragment key={partner.id}>
                    <PartnerItem
                      partner={partner}
                      hoverTitle="선택"
                      handleSelect={handleSelect}
                    />
                  </Fragment>
                ))}
              </div>
            </div>

            <UiComponent.Button
              color="primary"
              variant="contained"
              css={{ width: '120px', margin: '20px auto 0 auto' }}
              onClick={handleSubmit}
            >
              저장
            </UiComponent.Button>
          </ModalComponent.Modal>
        </ModalComponent.Background>
      </ModalComponent.Portal>
    </>
  );
}

function PartnerItem({
  partner,
  hoverTitle,
  handleSelect,
}: {
  hoverTitle: string;
  partner: IPartner;
  handleSelect: (partner: IPartner) => void;
}) {
  const [isHover, setIsHover] = useState(false);

  return (
    <div
      onClick={() => !partner.disabled && handleSelect(partner)}
      onMouseOver={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      css={{
        '> div > p': {
          color: '#fff',
          fontWeight: 'bold',
        },
      }}
    >
      <img src={partner.thumbnail} alt="" width={60} />
      {isHover && (
        <div>
          <p>{hoverTitle}</p>
        </div>
      )}
      {partner.disabled && (
        <div>
          <p>사용중지</p>
        </div>
      )}
    </div>
  );
}

export default Partner;
